import { QueryClient } from '@tanstack/react-query';
import { Translate } from '@tn/foundation/hooks/providers/useTranslation';
import { SUBSCRIBER_STATUSES, USER_STATUS } from '@tn/foundation/utils/constants/userStatus';
import request from 'graphql-request';
import { fromPromise } from 'xstate';

import { FlowEntity } from '../../types';
import { getUser } from '../helpers';
import { trackPartnerBindFailure, trackPartnerBindSuccess } from '../signin/tracking';

import { bindPartnerMutation } from './graphql/constants';

// Legacy PartnerService sessionStorage contract (web-package-coreui): the
// /account/partner-auth/:partner SPA writes these before handing the guest to
// sign-in; same-origin navigation to TN keeps them readable.
const BILLING_PARTNER_FLOW_KEY = 'PPLusBillingPartnerFlow';
const PARTNER_PREFIX = 'PARTNER_SERVICE_';
const CSP_VALUE_KEY = 'CSPVALUE';
const SUBSCRIPTION_DETAILS_KEY = 'SUBSCRIPTION_DETAILS';
const AUTH_ATTRIBUTES_KEY = 'AUTH_ATTRIBUTES';

// Sentinel for bind failures with no backend errorCode — the error modal maps
// unknown codes to the generic partner error copy, like legacy showPartnerError.
export const PARTNER_BIND_UNKNOWN_ERROR = 'UNKNOWN';
export const ACTIVE_USER_PACKAGE_EXISTS = 'ACTIVE_USER_PACKAGE_EXISTS';

export type PartnerBindContext = {
    subscriptionId: string;
    cspValue: string;
    displayName: string;
    hasOwnDevices: boolean;
};

const getStoredValue = <T>(key: string): T | null => {
    try {
        const raw = window.sessionStorage.getItem(PARTNER_PREFIX + key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
};

const inPartnerFlow = (): boolean => {
    try {
        return window.sessionStorage.getItem(BILLING_PARTNER_FLOW_KEY) === 'true';
    } catch {
        return false;
    }
};

/** Legacy PartnerService.unsetPartnerFlow parity. */
export const unsetPartnerFlow = (cspValue: string): void => {
    try {
        window.sessionStorage.setItem(BILLING_PARTNER_FLOW_KEY, 'false');
        if (cspValue && getStoredValue(cspValue)) {
            window.sessionStorage.setItem(PARTNER_PREFIX + cspValue, JSON.stringify(false));
        }
    } catch {
        // sessionStorage unavailable — nothing to clear
    }
};

/**
 * Reads the billing-partner state written by the legacy /partner-auth flow.
 * Returns null when the user is not in a partner flow — the "should we bind
 * on sign-in" signal (legacy SignIn.vue's inPartnerFlow branch).
 */
export const getPartnerBindContext = (): PartnerBindContext | null => {
    if (typeof window === 'undefined' || !inPartnerFlow()) {
        return null;
    }
    const subscriptionDetails = getStoredValue<{ subscriptionId?: string | number }>(
        SUBSCRIPTION_DETAILS_KEY,
    );
    if (!subscriptionDetails?.subscriptionId) {
        return null;
    }
    const authAttributes = getStoredValue<{ csp_display_name?: string; has_own_devices?: boolean }>(
        AUTH_ATTRIBUTES_KEY,
    );
    return {
        subscriptionId: String(subscriptionDetails.subscriptionId),
        cspValue: getStoredValue<string>(CSP_VALUE_KEY) ?? '',
        displayName: authAttributes?.csp_display_name ?? '',
        hasOwnDevices: !!authAttributes?.has_own_devices,
    };
};

/**
 * The partner flow only matters where the dtc machine will actually run the
 * bind (options.partnerBindEnabled — P+ only). The single predicate for the
 * dtc guard and the sign-in submit actor's refresh skip: on flag-off
 * brands/regions stray sessionStorage partner state must not divert the
 * normal sign-in completion. Same pattern as getEnabledMvpdSignInHandoff.
 */
export const getEnabledPartnerBindContext = (
    getConfigByKey: (key: string) => unknown,
): PartnerBindContext | null =>
    getConfigByKey('options.partnerBindEnabled') ? getPartnerBindContext() : null;

/**
 * Post-bind destination — port of legacy PartnerStore's
 * bindPartnerSuccessCallback. Partners with their own devices go to
 * rendezvous activation (flow flag kept for that page); others go home and
 * the partner flow is cleared. Null when not in a partner flow.
 *
 * `withRegion` prefixes the rendezvous route per the caller's region rules —
 * rendezvous lives at `/{region}/tv` on INTL but at `/tv` on US, where the
 * prefixed path 404s (legacy hardcoded the prefix and dead-ends US).
 */
export const consumePartnerSignInDestination = (
    withRegion: (route: string) => string,
): string | null => {
    const partnerContext = getPartnerBindContext();
    if (!partnerContext) {
        return null;
    }
    const urlParams = `?partner=${encodeURIComponent(partnerContext.displayName)}`;
    if (partnerContext.hasOwnDevices) {
        return withRegion(`/tv${urlParams}`);
    }
    unsetPartnerFlow(partnerContext.cspValue);
    return `/${urlParams}`;
};

const hasActivePackage = (queryClient: QueryClient): boolean => {
    const user = getUser(queryClient);
    const status = user?.userFullProfile?.userStatus?.description ?? '';
    // Legacy pre-bind check is isPartnerSubscription || isSubscriber; RESELLER
    // is the partner-subscription status.
    return SUBSCRIBER_STATUSES.includes(status) || status === USER_STATUS.RESELLER;
};

/**
 * Binds the partner subscription right after a successful sign-in — port of
 * legacy SignIn.vue's bindWithPartner branch, including the
 * already-subscribed short-circuit. Returns the partner error code to
 * surface, or null when the bind succeeded (or wasn't attempted because the
 * user is not in a partner flow).
 */
export const bindPartnerOnSignIn = async (
    partnerContext: PartnerBindContext,
    queryClient: QueryClient,
): Promise<string | null> => {
    // The bind intent is consumed once we attempt it — on failure, clear the
    // flow (like the success path does) so a later same-tab sign-in doesn't
    // rebind the stale subscriptionId, possibly onto a different account.
    const failWith = (errorCode: string): string => {
        unsetPartnerFlow(partnerContext.cspValue);
        return errorCode;
    };

    if (hasActivePackage(queryClient)) {
        trackPartnerBindFailure({
            errorCode: ACTIVE_USER_PACKAGE_EXISTS,
            message: 'accountBindingFailure_BillingPartners',
        });
        return failWith(ACTIVE_USER_PACKAGE_EXISTS);
    }

    try {
        // Plain request, not fetchQuery: a bind is a mutation and must fire
        // every time — a cached success:false response would replay as a
        // fresh failure on retry.
        const res = await request(`/api/tn/signup/graphql/`, bindPartnerMutation, {
            fields: { subscriptionId: partnerContext.subscriptionId },
        });

        if (res.bindPartner?.success) {
            // Deliberately NO profile refresh here: the full-page redirect
            // that follows reloads a fresh profile anyway, so a mid-flow
            // invalidation buys nothing. The purchase-complete event may
            // therefore read the pre-bind cbsPackageInfo.
            trackPartnerBindSuccess(queryClient);
            return null;
        }

        const errorCode = res.bindPartner?.errorCode || PARTNER_BIND_UNKNOWN_ERROR;
        trackPartnerBindFailure({ errorCode });
        return failWith(errorCode);
    } catch {
        trackPartnerBindFailure({ errorCode: PARTNER_BIND_UNKNOWN_ERROR });
        return failWith(PARTNER_BIND_UNKNOWN_ERROR);
    }
};

// Legacy PartnerMixin error map: backend bind errorCode → locale key prefix
// (`${prefix}_title` / `${prefix}_description`). Unknown codes fall back to
// the generic partner error copy.
const PARTNER_BIND_ERROR_KEY_PREFIXES: Record<string, string> = {
    ACTIVE_USER_PACKAGE_EXISTS: 'active_user_package_exists',
    PARTNER_SUBSCRIPTION_ALREADY_BOUND: 'partner_subscription_already_bound',
    PARTNER_SUBSCRIPTION_STATUS_INVALID: 'partner_subscription_status_invalid',
    PARTNER_SUBSCRIPTION_UNAUTHORIZED: 'partner_subscription_status_invalid',
    PRODUCT_NOT_FOUND: 'product_not_found',
    USER_CONNECTION_ALREADY_EXISTS: 'user_connection_already_exists',
};

/**
 * Copy for the bind-failure modal (legacy showPartnerError parity). Returns
 * final display strings — the formbuilder modal does not translate.
 */
export const getPartnerBindErrorCopy = (
    t: Translate['t'],
    errorCode: string,
    partner: string,
): { title: string; message: string } => {
    const prefix = PARTNER_BIND_ERROR_KEY_PREFIXES[errorCode];
    if (prefix) {
        return {
            title: t(`${prefix}_title`, { partner }),
            message: t(`${prefix}_description`, { partner }),
        };
    }
    return {
        title: t(
            'an_error_has_occurred_please_try_again_at_a_later_time',
            null,
            'An error has occurred, please try again at a later time.',
        ),
        message: t(
            'please_try_again_if_you_continue_to_encounter_this_issue_please_contact_your_partner_directly',
            null,
            'Please try again. If you continue to encounter this issue, please contact your partner directly.',
        ),
    };
};

export type PartnerBindOutput = {
    bound: boolean;
    errorCode?: string | null;
    /** Captured at bind time — a failed bind clears the partner flow, so the
     * sessionStorage context is gone by the time the error modal renders. */
    displayName?: string;
};

/**
 * Binds the billing-partner subscription to the just-signed-in account — the
 * TN counterpart of legacy SignIn.vue's bindWithPartner branch. Invoked by
 * the dtc machine's partnerBind state after a signIn done with the partner
 * sessionStorage flow present (gated by options.partnerBindEnabled + the
 * guard). Unlike the MVPD bind, a failure RESOLVES with the partner error
 * code so the dtc machine parks on the blocking error modal (legacy
 * showPartnerError parity) instead of surfacing a banner.
 */
export const partnerBindActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<PartnerBindOutput>(async () => {
            const partnerContext = getPartnerBindContext();
            if (!partnerContext) {
                // The dtc guard already checked; defensive for direct invokes.
                return { bound: false, errorCode: null };
            }
            const errorCode = await bindPartnerOnSignIn(partnerContext, queryClient);
            if (errorCode) {
                return { bound: false, errorCode, displayName: partnerContext.displayName };
            }
            return { bound: true };
        }),
};
